import * as S from './BehaviorAnalyticsPage.style';

const number = new Intl.NumberFormat('ko-KR');
const count = (value) => (value == null ? '—' : `${number.format(value)}명`);
const rate = (value) => (value == null ? '—' : `${number.format(value)}%`);

const WelcomeGiftFunnel = ({ data, collectionReady }) => {
    if (!data || !collectionReady)
        return (
            <S.StateCard>
                <div>
                    <h2>웰컴기프트 분석 데이터가 아직 준비되지 않았습니다</h2>
                    <p>수집된 화면 이동 기록이 준비되면 단계별 도달과 이탈을 확인할 수 있습니다.</p>
                </div>
            </S.StateCard>
        );
    const { summary, branches, commonStages } = data;
    return (
        <S.WelcomeContent>
            <S.Notice>화면 도달 기록을 사용자별로 집계합니다. 사진 업로드 성공, 신청 처리·기프트 발송 성공을 뜻하지 않습니다.</S.Notice>
            {data.measurement?.status === 'no_observed_events' && <S.FunnelEmpty>선택한 기간에는 수집된 웰컴기프트 화면 기록이 없습니다.</S.FunnelEmpty>}
            <S.KpiGrid>
                <Metric label="기프트 선택 화면 도달" value={count(summary.entryUsers)} hint="선택 기간 내 중복 사용자 제외" />
                <Metric label="완료 화면 도달" value={count(summary.completedUsers)} hint="기프트 선택 후 순서대로 완료 화면까지 도달" />
                <Metric label="전체 도달 전환율" value={rate(summary.completionRate)} hint="완료 화면 도달 ÷ 기프트 선택 화면 도달" />
                <Metric label="완료 화면 미도달" value={count(summary.entryUsers - summary.completedUsers)} hint="선택 기간 내 미도달 · 진행 중 사용자 포함" />
            </S.KpiGrid>
            <S.Card>
                <S.CardTitle>웰컴기프트 선택</S.CardTitle>
                <S.CardHelp>공통 진입 화면입니다. 상세 안내를 보지 않고 바로 신청할 수 있어, 상세 화면은 필수 단계에서 제외합니다.</S.CardHelp>
                <S.SegmentRow>
                    {commonStages.map((stage) => (
                        <S.SegmentChip key={stage.key}>
                            {stage.label}
                            <strong>{count(stage.users)}</strong>
                        </S.SegmentChip>
                    ))}
                </S.SegmentRow>
                {commonStages.map((stage) => (
                    <S.CardHelp key={stage.key}>
                        선택 → 신청자 정보 전환율 {rate(stage.conversionRate)} · 이탈 {count(stage.dropoffUsers)} ({rate(stage.dropoffRate)})
                    </S.CardHelp>
                ))}
            </S.Card>
            {branches.map((branch) => (
                <S.FunnelSection key={branch.key}>
                    <S.SectionHeading>
                        <div>
                            <S.CardTitle>{branch.label} 신청 퍼널</S.CardTitle>
                            <S.CardHelp>신청자 정보 화면부터 순서대로 도달한 사용자입니다. 같은 사용자의 재방문은 한 번만 집계합니다.</S.CardHelp>
                        </div>
                        <S.FunnelTotal>
                            완료 화면 도달 전환율<strong>{rate(branch.completionRate)}</strong>
                        </S.FunnelTotal>
                    </S.SectionHeading>
                    <S.SegmentRow>
                        <S.SegmentChip>
                            상세 안내 도달 <strong>{count(branch.detailUsers)}</strong>
                        </S.SegmentChip>
                        <S.SegmentChip>
                            신청 시작 <strong>{count(branch.startedUsers)}</strong>
                        </S.SegmentChip>
                        <S.SegmentChip>
                            중간 단계 진입 (앞 단계 기록 없음) <strong>{count(branch.resumeUsers)}</strong>
                        </S.SegmentChip>
                    </S.SegmentRow>
                    {branch.startedUsers === 0 && <S.FunnelEmpty>선택한 기간에는 신청자 정보 화면에서 시작한 경로가 없습니다.</S.FunnelEmpty>}
                    <S.FunnelTableWrap>
                        <S.FunnelTable aria-label={`${branch.label} 단계별 이탈`}>
                            <thead>
                                <tr>
                                    <th scope="col">단계</th>
                                    <th scope="col">도달 사용자</th>
                                    <th scope="col">도달 비율</th>
                                    <th scope="col">다음 단계 전환율</th>
                                    <th scope="col">이탈 사용자</th>
                                    <th scope="col">이탈률</th>
                                </tr>
                            </thead>
                            <tbody>
                                {branch.stages.map((stage, index) => (
                                    <tr key={stage.key}>
                                        <th scope="row">
                                            <S.StageLabel>
                                                <S.FunnelStepIndex>{index + 1}</S.FunnelStepIndex>
                                                {stage.label}
                                                {index === branch.stages.length - 1 ? ' 도달' : ''}
                                            </S.StageLabel>
                                        </th>
                                        <td>
                                            <strong>{count(stage.users)}</strong>
                                        </td>
                                        <td>
                                            <S.ReachBar $value={branch.startedUsers ? (stage.users / branch.startedUsers) * 100 : 0}>
                                                <span />
                                            </S.ReachBar>
                                            <small>{rate(branch.startedUsers ? Math.round((stage.users / branch.startedUsers) * 1000) / 10 : null)}</small>
                                        </td>
                                        <td>{rate(stage.conversionRate)}</td>
                                        <td>{count(stage.dropoffUsers)}</td>
                                        <td>
                                            <S.DropRate $warning={stage.dropoffRate > 0}>{rate(stage.dropoffRate)}</S.DropRate>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </S.FunnelTable>
                    </S.FunnelTableWrap>
                </S.FunnelSection>
            ))}
            <S.Card>
                <S.CardTitle>집계 기준</S.CardTitle>
                <S.MeasurementList>
                    <li>
                        상품별 퍼널은 신청자 정보 화면부터 집계하여 공통 진입·완료 사용자 합계와 다를 수 있습니다. 도달 비율은 신청 시작 사용자 기준, 전환율·이탈률은 현재 단계 사용자 기준이며, 분모가
                        없거나 마지막 단계이면 —로 표시합니다.
                    </li>
                    {(data.measurement?.limitations || []).map((item) => (
                        <li key={item}>{item}</li>
                    ))}
                </S.MeasurementList>
            </S.Card>
        </S.WelcomeContent>
    );
};
const Metric = ({ label, value, hint }) => (
    <S.KpiCard $tint="#efedff">
        <S.KpiLabel>{label}</S.KpiLabel>
        <S.KpiValue>{value}</S.KpiValue>
        <S.KpiHint>{hint}</S.KpiHint>
    </S.KpiCard>
);
export default WelcomeGiftFunnel;
